import type { GraphRelationship } from '../data/graph-types.js';
import type { GraphPath } from './graph.js';

export type StoryPlaybackStatus = 'idle' | 'playing' | 'paused' | 'completed';

export type StoryPlaybackPauseReason = 'user' | 'visibility';

export type StoryPlaybackState = {
  status: StoryPlaybackStatus;
  pathKey: string | null;
  stepCount: number;
  currentStepIndex: number;
  pauseReason: StoryPlaybackPauseReason | null;
  documentHidden: boolean;
};

export type StoryPlaybackAction =
  | { type: 'path-changed'; path: GraphPath | null }
  | { type: 'play' }
  | { type: 'pause' }
  | { type: 'resume' }
  | { type: 'previous' }
  | { type: 'next' }
  | { type: 'restart' }
  | { type: 'visibility-changed'; hidden: boolean };

export type StoryPlaybackStep = {
  index: number;
  number: number;
  total: number;
  sourceId: string;
  targetId: string;
  relationship: GraphRelationship;
};

function relationshipConnects(
  relationship: GraphRelationship,
  sourceId: string,
  targetId: string,
): boolean {
  return (
    (relationship.source === sourceId && relationship.target === targetId) ||
    (relationship.source === targetId && relationship.target === sourceId)
  );
}

/**
 * A playable path must describe at least one exact, ordered graph edge. This
 * guards playback consumers without introducing another path authority.
 */
export function isPlayableStoryPath(path: GraphPath | null): path is GraphPath {
  if (
    path === null ||
    path.relationships.length === 0 ||
    path.people.length !== path.relationships.length + 1
  ) {
    return false;
  }

  return path.relationships.every((relationship, index) => {
    const sourceId = path.people[index];
    const targetId = path.people[index + 1];
    return (
      sourceId !== undefined &&
      targetId !== undefined &&
      relationship.id.length > 0 &&
      relationshipConnects(relationship, sourceId, targetId)
    );
  });
}

/**
 * Stable for the lifetime of one exact graph. Relationship IDs and ordered
 * people distinguish alternative and reversed routes without storing a path
 * object in reducer state.
 */
export function storyPathKey(path: GraphPath | null): string | null {
  if (!isPlayableStoryPath(path)) {
    return null;
  }
  return JSON.stringify([path.people, path.relationships.map(({ id }) => id)]);
}

function resetStoryPlaybackState(
  path: GraphPath | null,
  documentHidden: boolean,
): StoryPlaybackState {
  const pathKey = storyPathKey(path);
  const stepCount = isPlayableStoryPath(path) ? path.relationships.length : 0;
  return {
    status: 'idle',
    pathKey,
    stepCount,
    currentStepIndex: 0,
    pauseReason: null,
    documentHidden,
  };
}

export function createStoryPlaybackState(
  path: GraphPath | null,
  options: { documentHidden?: boolean } = {},
): StoryPlaybackState {
  return resetStoryPlaybackState(path, options.documentHidden === true);
}

export function storyPlaybackReducer(
  state: StoryPlaybackState,
  action: StoryPlaybackAction,
): StoryPlaybackState {
  switch (action.type) {
    case 'path-changed': {
      const nextPathKey = storyPathKey(action.path);
      return nextPathKey === state.pathKey
        ? state
        : resetStoryPlaybackState(action.path, state.documentHidden);
    }

    case 'play':
      if (state.stepCount === 0 || state.documentHidden || state.status === 'playing') {
        return state;
      }
      return {
        ...state,
        status: 'playing',
        currentStepIndex: state.status === 'completed' ? 0 : state.currentStepIndex,
        pauseReason: null,
      };

    case 'pause':
      return state.status === 'playing'
        ? { ...state, status: 'paused', pauseReason: 'user' }
        : state;

    case 'resume':
      return state.status === 'paused' && !state.documentHidden
        ? { ...state, status: 'playing', pauseReason: null }
        : state;

    case 'previous': {
      if (state.stepCount === 0 || state.status === 'idle') {
        return state;
      }
      const currentStepIndex = Math.max(0, state.currentStepIndex - 1);
      if (currentStepIndex === state.currentStepIndex && state.status !== 'completed') {
        return state;
      }
      return {
        ...state,
        status: state.status === 'completed' ? 'paused' : state.status,
        currentStepIndex,
        pauseReason: state.status === 'completed' ? 'user' : state.pauseReason,
      };
    }

    case 'next': {
      if (state.stepCount === 0 || state.status === 'idle' || state.status === 'completed') {
        return state;
      }
      if (state.currentStepIndex >= state.stepCount - 1) {
        return { ...state, status: 'completed', pauseReason: null };
      }
      return { ...state, currentStepIndex: state.currentStepIndex + 1 };
    }

    case 'restart':
      if (state.stepCount === 0 || state.documentHidden) {
        return state;
      }
      return {
        ...state,
        status: 'playing',
        currentStepIndex: 0,
        pauseReason: null,
      };

    case 'visibility-changed':
      if (action.hidden === state.documentHidden) {
        return state;
      }
      if (action.hidden && state.status === 'playing') {
        return {
          ...state,
          status: 'paused',
          pauseReason: 'visibility',
          documentHidden: true,
        };
      }
      return { ...state, documentHidden: action.hidden };
  }
}

/** Returns the exact current edge only when state still belongs to this path. */
export function currentStoryPlaybackStep(
  path: GraphPath | null,
  state: StoryPlaybackState,
): StoryPlaybackStep | null {
  if (storyPathKey(path) !== state.pathKey || !isPlayableStoryPath(path)) {
    return null;
  }
  const relationship = path.relationships[state.currentStepIndex];
  const sourceId = path.people[state.currentStepIndex];
  const targetId = path.people[state.currentStepIndex + 1];
  if (relationship === undefined || sourceId === undefined || targetId === undefined) {
    return null;
  }
  return {
    index: state.currentStepIndex,
    number: state.currentStepIndex + 1,
    total: path.relationships.length,
    sourceId,
    targetId,
    relationship,
  };
}
